import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getAllRequests, deleteRequest } from "../../../store/request";
import { postPayment } from "../../../store/payment";
import { getUsers, getOneUser } from "../../../store/session";
import usersData from "../../../data/users.json";
import balanceService from "../../../utils/balanceService";

import './Notification.css';

const Notification = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [loaded, setLoaded] = useState(false);
  const users = Object.values(useSelector(state => state.session))
  const allRequests = Object.values(useSelector(state => state.requestState))
  const sessionUser = useSelector(state => state.session.user);
  
  // Get transaction data from users.json
  const transactions = usersData.transactions || [];
  const staticUsers = usersData.users || [];

  useEffect(() => {
    (async() => {
      await dispatch(getAllRequests());
      await dispatch(getUsers());
      setLoaded(true);
    })();
  }, [dispatch]);

  const onCreatePayment = async(e, amount, receiver_id, sender_id, title, privacy) => {
    e.preventDefault();

    if (title.length >= 1 && amount > 0) {
      await dispatch(postPayment({ amount, receiver_id, sender_id, title, privacy }))
      .then(() => {
        history.push('/')
      });
      await dispatch(getOneUser(sessionUser.id))
    }

  };

  const handleDelete = async (e, id) => {
    e.preventDefault();
    await dispatch(deleteRequest(id));
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserById = (id) => {
    return staticUsers.find(user => user.id === id);
  };

  // Get all transactions including new ones from balanceService
  const [allUserTransactions, setAllUserTransactions] = useState([]);
  
  useEffect(() => {
    const persistentTransactions = usersData.transactions || [];
    const newTransactions = balanceService.getAllTransactions() || [];
    
    // Combine transactions
    const combinedTransactions = [...newTransactions, ...persistentTransactions];
    
    // Remove duplicates and filter for current user
    const uniqueTransactions = combinedTransactions
      .filter((transaction, index, self) => 
        index === self.findIndex(t => t.id === transaction.id)
      )
      .filter(transaction => 
        transaction.receiver_id === (sessionUser?.id || 1) || transaction.sender_id === (sessionUser?.id || 1)
      )
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setAllUserTransactions(uniqueTransactions);
  }, [sessionUser?.id, transactions]);

  const userTransactions = allUserTransactions;

  const getNotificationText = (transaction) => {
    const otherUser = transaction.sender_id === (sessionUser?.id || 1) 
      ? getUserById(transaction.receiver_id)
      : getUserById(transaction.sender_id);
    
    const isIncoming = transaction.receiver_id === (sessionUser?.id || 1);
    const isPayment = transaction.type === 'payment';
    const isCharge = transaction.type === 'charge';
    const isRequest = transaction.type === 'request';
    
    if (isPayment && isIncoming) {
      return `${otherUser?.name || 'Someone'} paid you`;
    } else if (isPayment && !isIncoming) {
      return `You paid ${otherUser?.name || 'someone'}`;
    } else if (isCharge && isIncoming) {
      return `${otherUser?.name || 'Someone'} charged you`;
    } else if (isCharge && !isIncoming) {
      return `You charged ${otherUser?.name || 'someone'}`;
    } else if (isRequest && isIncoming) {
      return `${otherUser?.name || 'Someone'} requested money`;
    } else if (isRequest && !isIncoming) {
      return `You requested money from ${otherUser?.name || 'someone'}`;
    }
    
    return 'Transaction activity';
  };

  if (!loaded) {
    return null;
  };

  return (
    <div className="notification-page">
      <h3 className="notification-title">
        <i className="far fa-bell"></i> Notifications
      </h3>
      
      {/* Pending Requests Section */}
      {allRequests?.length > 0 && (
        <div className="requests-section">
          <h4 className="section-title">Pending Requests</h4>
          {allRequests?.map((request, i) =>
            <div key={i} className="requests-container">
              {(request.receiver_id === sessionUser?.id) ?
                <div className="requests">
                  <div className="requesting">
                    {`${users[request.sender_id - 1]?.name} requests`}
                  </div>
                  <div className="requesting-amount">
                    {`$${request.amount}`}
                  </div>
                  <div className="request-title">
                    {request.title}
                  </div>
                  <div className="request-btn">
                    <button className="edit-btn login-btn" onClick={e => {
                      onCreatePayment(e,
                                      request.amount,
                                      users[request.sender_id - 1]?.id,
                                      request.receiver_id,
                                      request.title,
                                      request.privacy)
                      handleDelete(e, request.id)
                      }
                    }>
                      Send
                    </button>
                    <button className="edit-btn login-btn cancel-btn" onClick={e => handleDelete(e, request.id)}>
                      Cancel
                    </button>
                  </div>
                </div>
                :
                <></>
              }
            </div>
          )}
        </div>
      )}
      
      {/* Transaction History Section */}
      <div className="transactions-section">
        <h4 className="section-title">Transaction History</h4>
        {userTransactions.length > 0 ? (
          <div className="transactions-list">
            {userTransactions.map((transaction) => {
              const otherUser = transaction.sender_id === (sessionUser?.id || 1) 
                ? getUserById(transaction.receiver_id)
                : getUserById(transaction.sender_id);
              
              return (
                <div key={transaction.id} className="transaction-notification">
                  <div className="transaction-avatar">
                    <img
                      src={otherUser?.profile_picture || 'https://i.pravatar.cc/40?img=1'}
                      alt={otherUser?.name || 'User'}
                    />
                  </div>
                  
                  <div className="transaction-details">
                    <div className="transaction-text">
                      {getNotificationText(transaction)}
                    </div>
                    
                    <div className="transaction-note">
                      {transaction.note}
                    </div>
                    
                    <div className="transaction-meta">
                      <span className="transaction-amount">
                        {formatCurrency(transaction.amount)}
                      </span>
                      <span className="transaction-time">
                        {formatDate(transaction.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {transaction.status === 'pending' && (
                    <div className="transaction-status">
                      <span className="status-pending">Pending</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-transactions">
            <p>No transaction history available</p>
          </div>
        )}
      </div>
    </div>
  )
}
export default Notification;
